import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import './ItemLoss.css';

const LOSS_TYPES = ['Evaporation', 'Spillage', 'Testing', 'Leakage', 'Other'];
const EMPTY = { loss_date: today(), product_code: 'HSD', loss_type: 'Evaporation', qty: '', rate: '' };

export default function ItemLoss() {
  const { can, canAccess } = useAuth();
  const isAdmin = can('item_loss', 'approve');
  const { data: products } = useApi(() => api.products.list(), []);
  const { data: records, loading, error, reload } = useApi(() => api.itemLoss.list(), []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const isToday = (d) => d === today();

  const handleSave = async () => {
    if (!form.qty || !form.rate) { notify('❌ Qty and Rate required'); return; }
    setSaving(true);
    try {
      await api.itemLoss.create({ ...form, qty: parseFloat(form.qty), rate: parseFloat(form.rate) });
      notify('✅ Loss entry saved'); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await api.itemLoss.update(editRow.id, { product_code: editRow.product_code, loss_type: editRow.loss_type, qty: parseFloat(editRow.qty), rate: parseFloat(editRow.rate) });
      notify('✅ Updated'); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try { await api.itemLoss.remove(confirm.row.id); notify('✅ Deleted'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const total = (records || []).reduce((s, r) => s + Number(r.amount || 0), 0);
  if (!canAccess('item_loss')) {
    return (
      <div className="item-loss-page">
        <ErrBanner msg="Access Denied: You do not have permission to access item loss." />
      </div>
    );
  }
  return (
    <div className="item-loss-page">
      <div className="page-header">
        <h2 className="page-title">Item Loss</h2>
        {can('item_loss', 'create') && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
            ＋ Add Loss Entry
          </button>
        )}      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">New Loss Entry</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label>
              <input type="date" className="form-control" value={form.loss_date} onChange={e => setForm({ ...form, loss_date: e.target.value })} />
            </div>
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={form.product_code} onChange={e => setForm({ ...form, product_code: e.target.value })}>
                {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Loss Type</label>
              <select className="form-control" value={form.loss_type} onChange={e => setForm({ ...form, loss_type: e.target.value })}>
                {LOSS_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Qty (Ltr)</label>
              <input type="number" className="form-control" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>Rate (₹)</label>
              <input type="number" className="form-control" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} />
            </div>
            {form.qty && form.rate && (
              <div className="form-group balance-group"><label>Amount</label>
                <div className="balance-value">₹{fmtNum(parseFloat(form.qty) * parseFloat(form.rate))}</div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save'}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Loss Entry</div>
          <div className="form-row">
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={editRow.product_code} onChange={e => setEditRow({ ...editRow, product_code: e.target.value })}>
                {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Loss Type</label>
              <select className="form-control" value={editRow.loss_type} onChange={e => setEditRow({ ...editRow, loss_type: e.target.value })}>
                {LOSS_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Qty (Ltr)</label>
              <input type="number" className="form-control" value={editRow.qty} onChange={e => setEditRow({ ...editRow, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>Rate (₹)</label>
              <input type="number" className="form-control" value={editRow.rate} onChange={e => setEditRow({ ...editRow, rate: e.target.value })} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : '💾 Update'}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {!loading && (
        <div className="section-card">
          <div className="section-header">Loss Records — Total: ₹{fmtNum(total)}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>Date</th><th style={{ textAlign: 'left' }}>Product</th>
                <th style={{ textAlign: 'left' }}>Loss Type</th><th>Qty (Ltr)</th>
                <th>Rate (₹)</th><th>Amount (₹)</th><th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(records || []).map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td><td>{r.loss_date}</td>
                    <td><span className="badge badge-info">{r.product_code}</span></td>
                    <td>{r.loss_type}</td>
                    <td className="right-align">{r.qty}</td>
                    <td className="right-align">₹{fmtNum(r.rate)}</td>
                    <td className="right-align">₹{fmtNum(r.amount)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {can('item_loss', 'update') && (isAdmin || isToday(r.loss_date)) && (
                        <button title="Edit" style={btnEdit} onClick={() => setEditRow({ ...r })}>✏️</button>
                      )}
                      {can('item_loss', 'delete') && (

                        <button title="Delete" style={btnDel} onClick={() => setConfirm({ row: r })}>🗑️</button>)}
                    </td>
                  </tr>
                ))}
                {(records || []).length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>No loss entries recorded.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Loss Entry"
        message="Are you sure you want to delete this loss entry? The record will be soft-deleted."
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
